import { printHtml } from "@/components/inventory/shelfPrint";
import { moNum } from "@/lib/moNum";

const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const fmtN = (n, d = 0) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
const fmtDate = (s) => {
  if (!s) return "";
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${Number(m[2])}/${Number(m[3])}/${m[1]}` : String(s);
};

const CSS = `
  .po{font-family:'Times New Roman',Times,serif;color:#000;font-size:13px}
  .po .lbl{font-family:Arial,Helvetica,sans-serif}
  .po .hdrrow{display:flex;justify-content:space-between;align-items:flex-start}
  .po .co{font-size:15px;line-height:1.5;padding-left:14px}
  .po .title{font-family:Arial,Helvetica,sans-serif;font-size:27px;font-weight:bold;text-align:right}
  .po table{border-collapse:collapse}
  .po .mini{margin-top:6px;margin-left:auto}
  .po .mini td{border:1px solid #000;text-align:center;padding:4px 14px;font-size:12px}
  .po .mini .h{font-family:Arial,Helvetica,sans-serif;font-size:11.5px}
  .po .parties{display:flex;gap:28px;margin-top:52px;padding:0 14px}
  .po .party{width:46%;border:1px solid #000;min-height:128px}
  .po .party .ph{font-family:Arial,Helvetica,sans-serif;font-size:12px;border-bottom:1px solid #000;padding:5px 10px}
  .po .party .pv{padding:6px 10px;font-size:12.5px;line-height:1.5}
  .po .items{width:100%;margin-top:34px}
  .po .items th{border:1px solid #000;font-family:Arial,Helvetica,sans-serif;font-weight:normal;font-size:12px;padding:5px 6px;text-align:center}
  .po .items td{border:1px solid #000;vertical-align:top;padding:5px 7px;font-size:12.5px;height:370px}
  .po .items .r{text-align:right}
  .po .totrow{display:flex;justify-content:flex-end}
  .po .totbox{display:flex;align-items:center;justify-content:space-between;width:41%;border:1px solid #000;border-top:none;padding:7px 10px}
  .po .totbox .t{font-family:Arial,Helvetica,sans-serif;font-size:19px;font-weight:bold}
  .po .totbox .a{font-size:12.5px}
  .po .notes{border:1px solid #000;width:57%;height:105px;margin-top:40px}
`;

const buildHtml = (mo) => {
  const sizeM = String(mo.product_name || "").match(/([\d.]+)\s*oz/i);
  const sizeOz = sizeM ? `${sizeM[1]}oz` : (mo.bottle_size || "");
  const matM = String(mo.product_name || "").match(/^(PET|ALUM)/i);
  const material = matM ? matM[1].toUpperCase() : "";
  const pack = mo.case_pack ? Math.round(mo.case_pack) : 0;
  const bottles = Number(mo.ordered_bottles || mo.bottle_quantity || 0);
  const cases = mo.unit === "cases" && mo.quantity ? Number(mo.quantity) : (pack ? Math.floor(bottles / pack) : 0);
  const bpp = Number(mo.bottles_per_pallet || 0);
  const pallets = bpp ? Math.round((bottles / bpp) * 10) / 10 : null;
  const itemText = [mo.design_name, sizeOz && pack ? `${sizeOz}/${pack}pk` : sizeOz, material].filter(Boolean).join(" ");
  const price = mo.price_per_case != null && mo.price_per_case !== "" ? Number(mo.price_per_case) : null;
  const amount = price != null ? cases * price : null;
  const produceFor = mo.customer_po
    ? [mo.customer_name, `Customer PO ${mo.customer_po}`].filter(Boolean).map(esc).join("<br/>")
    : '<span style="color:#555;font-style:italic">Projection — no customer PO</span>';
  const descLines = [
    esc(itemText),
    "",
    `Cases: ${fmtN(cases)}`,
    `Bottles: ${fmtN(bottles)}`,
    pallets != null ? `Pallets: ${fmtN(pallets, Number.isInteger(pallets) ? 0 : 1)}` : "",
  ].join("<br/>");
  const body = `
  <div class="po">
    <div class="hdrrow">
      <div class="co"><div>WaterCo LLC</div><div>New York, NY 10017</div><div>575 Fifth Ave</div></div>
      <div style="width:46%">
        <div class="title">Production Order</div>
        <table class="mini"><tr><td class="h">Order Date</td><td class="h">Production #</td></tr>
          <tr><td>${esc(fmtDate(mo.created_at))}</td><td>${esc(moNum(mo.mwo_number))}</td></tr></table>
        <table class="mini" style="margin-top:22px"><tr><td class="h" style="padding:4px 34px">Ship Date</td></tr>
          <tr><td>${esc(fmtDate(mo.source_rdd || mo.production_date))}</td></tr></table>
      </div>
    </div>
    <div class="parties">
      <div class="party"><div class="ph">Bottler</div><div class="pv">WaterCo Springs</div></div>
      <div class="party"><div class="ph">Produce For:</div><div class="pv">${produceFor}</div></div>
    </div>
    <table class="items">
      <tr><th style="width:26%">Item</th><th style="width:33%">Description</th><th style="width:10%">Qty</th><th style="width:15%">Price</th><th style="width:16%">Amount</th></tr>
      <tr>
        <td>${esc(itemText)}</td>
        <td>${descLines}</td>
        <td class="r">${fmtN(cases)}</td>
        <td class="r">${price != null ? fmtN(price, 2) : ""}</td>
        <td class="r">${amount != null ? fmtN(amount, 2) : ""}</td>
      </tr>
    </table>
    <div class="totrow"><div class="totbox"><span class="t">Total</span><span class="a">USD ${fmtN(amount || 0, 2)}</span></div></div>
    <div class="notes"></div>
  </div>`;
  return `<html><head><title>${esc(moNum(mo.mwo_number))} Production Order</title>
  <style>@page{size:letter;margin:12mm} body{margin:0} ${CSS}</style></head><body>${body}</body></html>`;
};

export const printProductionOrder = (mo, company = {}) => {
  printHtml(buildHtml(mo));
};
